import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  useTheme,
  Chip,
  Stack,
  Pagination,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  CalendarToday,
  ArrowForward,
  Share,
  Bookmark,
  Person,
  TrendingUp,
  NewReleases,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';

const News = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('all');

  const news = [
    {
      id: 1,
      title: 'GlobalComm Launches New Trans-Atlantic Submarine Cable',
      summary: 'New high-capacity cable system to connect North America and Europe with 200Tbps capacity, reducing latency by 30%.',
      date: 'March 15, 2024',
      category: 'Press Release',
      image: 'https://images.unsplash.com/photo-1557324232-a8917cb3c255?w=600&h=300&fit=crop',
      author: 'Sarah Johnson',
      readTime: '5 min read',
      featured: true,
    },
    {
      id: 2,
      title: 'Company Named Best Global Carrier 2024',
      summary: 'Recognized for excellence in telecommunications and customer service at annual industry awards ceremony in London.',
      date: 'February 28, 2024',
      category: 'Award',
      image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&h=300&fit=crop',
      author: 'Michael Chen',
      readTime: '3 min read',
      featured: false,
    },
    {
      id: 3,
      title: 'Expansion into African Markets',
      summary: 'New data centers and fiber routes announced across key African economic hubs including Nairobi, Lagos, and Johannesburg.',
      date: 'February 10, 2024',
      category: 'Announcement',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=300&fit=crop',
      author: 'David Williams',
      readTime: '4 min read',
      featured: true,
    },
    {
      id: 4,
      title: '5G Network Coverage Reaches 100 Cities',
      summary: 'Milestone achievement in our 5G rollout strategy across major metropolitan areas worldwide.',
      date: 'January 25, 2024',
      category: 'Technology',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&h=300&fit=crop',
      author: 'Emily Zhang',
      readTime: '6 min read',
      featured: false,
    },
    {
      id: 5,
      title: 'Sustainable Energy Initiative Launched',
      summary: 'Commitment to power all data centers with renewable energy by 2025 announced at UN Climate Conference.',
      date: 'January 15, 2024',
      category: 'Sustainability',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600&h=300&fit=crop',
      author: 'Lisa Anderson',
      readTime: '4 min read',
      featured: false,
    },
    {
      id: 6,
      title: 'Partnership with Major Cloud Provider',
      summary: 'Strategic partnership to offer enhanced cloud connectivity solutions for enterprise customers.',
      date: 'January 5, 2024',
      category: 'Partnership',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=300&fit=crop',
      author: 'James Wilson',
      readTime: '3 min read',
      featured: false,
    },
  ];

  const categories = ['all', 'Press Release', 'Award', 'Announcement', 'Technology', 'Sustainability', 'Partnership'];
  
  const filteredNews = news.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.summary.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = category === 'all' || item.category === category;
    return matchesSearch && matchesCategory;
  });

  const featuredNews = filteredNews.filter(item => item.featured);
  const regularNews = filteredNews.filter(item => !item.featured);

  const handleCategoryChange = (event, newValue) => {
    setCategory(newValue);
  };

  return (
    <Box>
      <PageHeader 
        title="News & Press" 
        subtitle="Latest updates from our company"
      />

      <Container maxWidth="xl" sx={{ py: 8 }}>
        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Card sx={{ p: 3, mb: 6 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search news..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Tabs
                  value={category}
                  onChange={handleCategoryChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                    },
                  }}
                >
                  {categories.map((cat) => (
                    <Tab key={cat} label={cat.charAt(0).toUpperCase() + cat.slice(1)} value={cat} />
                  ))}
                </Tabs>
              </Grid>
            </Grid>
          </Card>
        </motion.div>

        {/* Featured News */}
        {featuredNews.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
              Featured Stories
            </Typography>
            <Grid container spacing={3}>
              {featuredNews.map((item, index) => (
                <Grid item xs={12} md={6} key={item.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%' }}>
                      <CardMedia
                        component="img"
                        sx={{ width: { xs: '100%', md: 280 }, height: 200, objectFit: 'cover' }}
                        image={item.image}
                        alt={item.title}
                      />
                      <CardContent sx={{ flex: 1 }}>
                        <Chip 
                          label={item.category} 
                          size="small" 
                          sx={{ mb: 1, bgcolor: theme.palette.primary.light + '20', color: theme.palette.primary.main }}
                        />
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {item.summary}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {item.date}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {item.author}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {item.readTime}
                          </Typography>
                        </Stack>
                        <Button
                          size="small"
                          endIcon={<ArrowForward />}
                          sx={{ mt: 2 }}
                          onClick={() => navigate(`/news/${item.id}`)}
                        >
                          Read More
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Latest News */}
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
            Latest News
          </Typography>
          <Grid container spacing={3}>
            {regularNews.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={item.image}
                      alt={item.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Chip 
                        label={item.category} 
                        size="small" 
                        sx={{ mb: 1, bgcolor: theme.palette.primary.light + '20', color: theme.palette.primary.main }}
                      />
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {item.summary.substring(0, 120)}...
                      </Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {item.date}
                          </Typography>
                        </Stack>
                        <Button
                          size="small"
                          endIcon={<ArrowForward />}
                          onClick={() => navigate(`/news/${item.id}`)}
                        >
                          Read
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {filteredNews.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              No news found matching your criteria
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        {filteredNews.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination count={3} color="primary" size="large" />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default News;